// ... (imports and initial setup unchanged)
import { useCallback, useEffect, useRef, useState } from "react";
import "../../styles/UserHome.css";
import "../../styles/Form.css";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { LuBadgeCheck, LuCalendarX, LuCircleDashed, LuTimerReset } from "react-icons/lu";
import Select from "react-select";
import DonutFileStatusChart from "../../components/DonutFileStatusChart";
import { fetchLatestTaskStatus, getTaskDisplayStatus } from "../../utils/taskStatus";
import { ageFromDateOnly, toDateOnly } from "../../utils/dateOnly";
import { formatPhoneNumber, phoneHref } from "../../utils/phone";
import useAccess from "../../hooks/useAccess";

const DEFAULT_FORM = {
  first_name: "",
  mid_name: "",
  last_name: "",
  email: "",
  mobile: "",
  home_phone: "",
  address1: "",
  address2: "",
  city: "",
  province: "",
  country: "",
  postal_code: "",
  dob: "",
  profilePic: "",
  userid_no: "",
  gender: "",
  sin: "",
  user_hiring_date: "",
};

function getStoredCurrentUser(isOwnHome) {
  if (!isOwnHome) return null;

  const raw =
    localStorage.getItem("me") ||
    localStorage.getItem("user") ||
    localStorage.getItem("currentUser");

  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch (error) {
    console.warn("Failed to parse cached user profile:", error);
    return null;
  }
}

function UserHome() {
  const NO_COMPLETE_TASK_IDS = new Set([27, 29, 33, 34, 35, 41]);
  const HIDDEN_HOME_TASK_KEYS = new Set([
    "backgroundcheck",
    "background_check_fcra",
    "consent_history",
    "psp_disclosure_and_authorization",
    "summary_of_rights_under_fcra",
  ]);

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const loggedInUserId = localStorage.getItem("userId");
  const userId = id || loggedInUserId;
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const currentUserId = Number(loggedInUserId);
  const isAdmin = localStorage.getItem("userRole") === "1";
  const isOwnHome = !id || Number(id) === currentUserId;
  const { hasPermission, permissionsLoaded } = useAccess();
  const canViewSin = !isAdmin || hasPermission("Task", "SIN", "view");
  const isSinTask = (task) => {
    const key = String(task?.t_key || "").toLowerCase();
    const title = String(task?.t_title || "").toLowerCase();
    return key === "sin" || key.includes("social_insurance") || /\bs\.?i\.?n\.?\b/.test(title);
  };
  const canViewTask = (task) => {
    if (!isAdmin) return true;
    if (isSinTask(task)) return canViewSin;
    const resource = `Task:${task?.t_key || "unknown"}`;
    return hasPermission("Task", resource, "view");
  };

  const [form, setForm] = useState(() => ({
    ...DEFAULT_FORM,
    ...(getStoredCurrentUser(isOwnHome) || {}),
  }));

  const [selectedTraining, setSelectedTraining] = useState("");
  const [taskStatus, setTaskStatus] = useState({});
  const [statuses, setStatuses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesState, setCategoriesState] = useState("loading");
  const [profileLoad, setProfileLoad] = useState({ userId: null, status: "loading" });
  const [orgUnits, setOrgUnits] = useState([]);
  const [assignedTrainings, setAssignedTrainings] = useState([]);
  const [assignedGroups, setAssignedGroups] = useState([]); // [{category:{id,name}, tasks:[...] }]
  const [showSin, setShowSin] = useState(false);
  const [isUploadingProfile, setIsUploadingProfile] = useState(false);
  const [profileImageBroken, setProfileImageBroken] = useState(false);
  const profileUploadInputRef = useRef(null);
  const assignedTasksRequestRef = useRef(0);
  const placeholderLabel = isAdmin ? "Upload Image" : "User";

  const formatSin = (sin, show) => {
    if (!sin) return "-";
    if (show) return sin;

    const last4 = sin.slice(-4);
    return `*****${last4}`;
  };

  const handleProfileUpload = async (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.type.startsWith("image/")) {
      alert("Please select an image file.");
      event.target.value = "";
      return;
    }

    try {
      setIsUploadingProfile(true);

      const formData = new FormData();
      formData.append("profilePic", selectedFile);
      formData.append("userId", userId);

      const res = await axios.put(
        `${BASE_URL}/api/users/${userId}/profile-picture?userId=${userId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      const updatedProfilePic = res.data?.profilePic || "";
      setForm((prev) => ({ ...prev, profilePic: updatedProfilePic }));

      if (isOwnHome) {
        const updatedUser = { ...form, profilePic: updatedProfilePic };
        localStorage.setItem("me", JSON.stringify(updatedUser));
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error("Failed to upload profile picture.", error);
      alert("Failed to upload profile picture.");
    } finally {
      setIsUploadingProfile(false);
      event.target.value = "";
    }
  };

  const handleTrainingSelect = (option) => {
    const value = option?.value || "";
    setSelectedTraining(value);
    if (!value) return;

    // 🔹 Internal training
    navigate(`/training/${userId}`, {
      state: {
        openModuleKey: value,
      },
    });
  };

  const hasProfileImage = Boolean(form.profilePic);
  const showProfileImage = hasProfileImage && !profileImageBroken;
  const profileImageUrl = hasProfileImage
    ? `${BASE_URL}/uploads/profilepic/${form.profilePic}`
    : null;

  useEffect(() => {
    setProfileImageBroken(false);
  }, [form.profilePic]);

  const loadAssignedTasks = useCallback(async () => {
    if (!userId) return;

    const requestId = assignedTasksRequestRef.current + 1;
    assignedTasksRequestRef.current = requestId;

    try {
      const res = await axios.get(`${BASE_URL}/api/tasks/assigned/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        params: { _: Date.now() },
      });
      if (assignedTasksRequestRef.current !== requestId) return;

      const rows = res.data?.tasks || [];

      // Group by category
      const byCat = new Map();
      rows.forEach((r) => {
        const k = r.t_category_id;
        if (!byCat.has(k)) {
          byCat.set(k, {
            category: {
              id: r.t_category_id,
              name: r.tc_name,
              key: r.tc_key, // e.g. 'employment_record', 'compliance'
              order: r.tc_order ?? 9999,
            },
            tasks: [],
          });
        }
        byCat.get(k).tasks.push({ ...r, order: r.t_order ?? 9999 });
      });

      // 1) sort tasks within each category
      for (const g of byCat.values()) {
        g.tasks.sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));
      }

      // 2) force Employment Record first, Compliance second, then tc_order
      const PRIORITY = { employment_record: 0, compliance: 1 };
      const sortedGroups = Array.from(byCat.values()).sort((a, b) => {
        const pa = PRIORITY[a.category.key] ?? 100;
        const pb = PRIORITY[b.category.key] ?? 100;
        if (pa !== pb) return pa - pb;
        if ((a.category.order ?? 9999) !== (b.category.order ?? 9999)) {
          return (a.category.order ?? 9999) - (b.category.order ?? 9999);
        }
        return String(a.category.name).localeCompare(String(b.category.name));
      });

      setAssignedGroups(sortedGroups);
    } catch (error) {
      if (assignedTasksRequestRef.current !== requestId) return;
      console.error("Failed to load assigned tasks", error);
    }
  }, [userId, BASE_URL]);

  useEffect(() => {
    loadAssignedTasks().catch(console.error);
  }, [loadAssignedTasks, location.key]);

  // 1) bring back the profile fetch so setForm is used
  useEffect(() => {
    if (!userId) return;

    let active = true;
    setProfileLoad({ userId: String(userId), status: "loading" });
    const token = localStorage.getItem("token");
    const cachedUser = getStoredCurrentUser(isOwnHome);

    if (cachedUser) {
      setForm((prev) => ({ ...prev, ...cachedUser }));
    }

    const loadProfile = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!active) return;
        setForm({ ...DEFAULT_FORM, ...res.data });
        setProfileLoad({ userId: String(userId), status: "ready" });

        if (isOwnHome) {
          localStorage.setItem("me", JSON.stringify(res.data));
          localStorage.setItem("user", JSON.stringify(res.data));
        }
      } catch (error) {
        if (!active) return;
        if (!isOwnHome) {
          setProfileLoad({ userId: String(userId), status: "error" });
          console.error("Failed to load user profile.", error);
          return;
        }

        try {
          const res = await axios.get(`${BASE_URL}/api/user/profile`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (!active) return;
          setForm((prev) => ({ ...prev, ...res.data }));
          setProfileLoad({ userId: String(userId), status: "ready" });
        } catch (fallbackError) {
          if (!active) return;
          setProfileLoad({ userId: String(userId), status: "error" });
          console.error("Failed to load user home profile.", fallbackError);
        }
      }
    };

    loadProfile();
    return () => { active = false; };
  }, [userId, BASE_URL, isOwnHome]);

  // 2) define openTask (you call it in the onClick)
  const openTask = (task) => {
    if (!canViewTask(task)) return;
    const viewingUserId = userId; // target user

    if (task.t_key === "consent_history") {
      // ✅ go to list to choose which employer (regular flow, not slide)
      navigate(`/consent/list/${viewingUserId}`);
      return;
    }

    // your existing special case for user_information (profile vs edit)
    if (task.t_key === "user_information") {
      if (isAdmin && currentUserId !== Number(viewingUserId)) {
        navigate(`/edit-user/${viewingUserId}`);
      } else {
        navigate(`/profile`);
      }
      return;
    }

    // default
    if (!task.t_route) return;
    navigate(task.t_route.replace(":userId", viewingUserId));
  };

  const getTaskStatus = (task) => getTaskDisplayStatus(task, taskStatus);

  const isDone = (task) => getTaskStatus(task) === "completed";
  const isReview = (task) => getTaskStatus(task) === "review";
  const isAlmostExpired = (task) => getTaskStatus(task) === "almost_expired";
  const isExpired = (task) => getTaskStatus(task) === "expired";
  const needsComplete = (task) => {
    const taskId = Number(task?.t_id);
    if (NO_COMPLETE_TASK_IDS.has(taskId)) return false;
    return Number(task?.t_need_complete ?? 1) === 1;
  };

  // Count every visible assigned task that uses a completion status. Filtering
  // whole categories caused valid statuses (including almost expired) to be
  // shown beside a task but omitted from the Task Status summary.
  const assignedTaskList = assignedGroups.flatMap((group) =>
    group.tasks.filter(
      (task) =>
        needsComplete(task) &&
        canViewTask(task) &&
        Number(task.t_admin_only ?? 0) !== 1 &&
        !HIDDEN_HOME_TASK_KEYS.has(String(task.t_key || "").toLowerCase()),
    ),
  );

  // DB-driven totals
  const assignedTotal = assignedTaskList.length;
  const completedCount = assignedTaskList.filter((t) => isDone(t)).length;
  const almostExpiredCount = assignedTaskList.filter((t) => isAlmostExpired(t)).length;
  const expiredCount = assignedTaskList.filter((t) => isExpired(t)).length;
  const reviewCount = assignedTaskList.filter((t) => isReview(t)).length;
  const notStartedCount = Math.max(
    assignedTotal - completedCount - almostExpiredCount - expiredCount - reviewCount,
    0,
  );

  const fetchTaskStatus = useCallback(async () => {
    if (!userId) return;

      try {
        const res = await axios.get(
          `${BASE_URL}/api/usertasks/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        const token = localStorage.getItem("token");
        const baseStatus = res.data.status || {};

        // Vacation
        await fetchLatestTaskStatus({
          baseUrl: BASE_URL,
          userId,
          endpoint: "/api/vacationform/latest/status",
          taskId: 1,
          taskKey: "vacation_form",
          token,
          baseStatus,
        });

        await fetchLatestTaskStatus({
          baseUrl: BASE_URL,
          userId,
          endpoint: "/api/driverlicense/latest/status",
          taskId: 22,
          taskKey: "driver_license",
          token,
          baseStatus,
        });

        await fetchLatestTaskStatus({
          baseUrl: BASE_URL,
          userId,
          endpoint: "/api/driverabstract/latest/status",
          taskId: 80,
          taskKey: "driver_abstract",
          token,
          baseStatus,
        });

        await fetchLatestTaskStatus({
          baseUrl: BASE_URL,
          userId,
          endpoint: "/api/driverlicense/latest/status",
          taskId: 22,
          taskKey: "driver_license",
          token,
          baseStatus,
        });

        // Annual Driver Certification ✅
        await fetchLatestTaskStatus({
          baseUrl: BASE_URL,
          userId,
          endpoint: "/api/annual-driver-certifications/latest/status",
          taskId: 57,
          taskKey: "annual_driver_certification",
          token,
          baseStatus,
        });

        // Driver Disclosure ✅
        await fetchLatestTaskStatus({
          baseUrl: BASE_URL,
          userId,
          endpoint: "/api/driver-disclosures/latest/status",
          taskId: 58,
          taskKey: "driver_disclosure",
          token,
          baseStatus,
        });

        // Existing Consent History logic...
        const employersRes = await axios.get(
          `${BASE_URL}/api/work-history/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        const employers = employersRes.data || [];
        const workingEmployers = employers.filter(
          (emp) => emp.wh_type === "Working",
        );
        const requiredConsentTasks = workingEmployers.map(
          (emp) => `consent_history_${emp.wh_id}`,
        );
        if (requiredConsentTasks.length > 0) {
          if (
            requiredConsentTasks.every(
              (task) => baseStatus[task] === "completed",
            )
          ) {
            baseStatus["consent_history"] = "completed";
          } else if (
            requiredConsentTasks.every((task) => baseStatus[task] === "review")
          ) {
            baseStatus["consent_history"] = "review";
          } else {
            baseStatus["consent_history"] = "not_completed";
          }
        }

        setTaskStatus(baseStatus);
      } catch (error) {
        console.error("Failed to fetch task statuses or employer list", error);
      }
  }, [BASE_URL, userId]);

  useEffect(() => {
    fetchTaskStatus();
  }, [fetchTaskStatus, location.key]);

  useEffect(() => {
    const refreshHomeData = () => {
      loadAssignedTasks().catch(console.error);
      fetchTaskStatus();
    };

    window.addEventListener("focus", refreshHomeData);
    window.addEventListener("pageshow", refreshHomeData);

    return () => {
      window.removeEventListener("focus", refreshHomeData);
      window.removeEventListener("pageshow", refreshHomeData);
    };
  }, [fetchTaskStatus, loadAssignedTasks]);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/dropdowns/user-status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((res) => setStatuses(res.data))
      .catch((err) => console.error("❌ Failed to fetch user statuses:", err));
  }, [BASE_URL]);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/dropdowns/categories`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((res) => {
        setCategories(res.data);
        setCategoriesState("ready");
      })
      .catch((err) => {
        setCategoriesState("error");
        console.error("❌ Failed to fetch categories:", err);
      });
  }, [BASE_URL]);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/dropdowns/org-units`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((res) => setOrgUnits(res.data))
      .catch((err) => console.error("❌ Failed to fetch Org Units:", err));
  }, [BASE_URL]);

  useEffect(() => {
    if (!userId) return;

    const load = async () => {
      try {
        const token = localStorage.getItem("token");

        const res = await axios.get(`${BASE_URL}/api/trainings/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const internal = res.data.internalModules || [];

        setAssignedTrainings(internal);
      } catch (e) {
        console.error("Failed to load assigned trainings", e);
      }
    };

    load();
  }, [userId, BASE_URL]);

  const getCategoryName = (categoryId) => {
    const cat = categories.find((c) => c.c_id === parseInt(categoryId));
    return cat ? cat.c_name : categoryId; // fallback to ID if not found
  };

  const getStatusName = (statusId) => {
    const status = statuses.find((item) => item.s_id === parseInt(statusId));
    return status?.s_TranslatedText || status?.s_name || statusId;
  };

  const getOrgUnitName = (orgUnitId) => {
    const ou = orgUnits.find((ou) => ou.ou_id === parseInt(orgUnitId));
    return ou ? ou.ou_name : orgUnitId; // fallback to ID if not found
  };

  const isOwnerOperatorCategory = String(getCategoryName(form.emp_category) || "")
    .toLowerCase().replace(/[^a-z0-9]/g, "") === "owneroperator";

  const profileIsReady = profileLoad.userId === String(userId) && profileLoad.status === "ready";
  const actionsReady = permissionsLoaded && profileIsReady && categoriesState === "ready";
  const actionsFailed = (profileLoad.userId === String(userId) && profileLoad.status === "error") || categoriesState === "error";

  const fullName = [form.first_name, form.mid_name, form.last_name]
    .filter(Boolean)
    .join(" ");
  const fullAddress = [
    form.address1,
    form.address2,
    form.city,
    form.province,
    form.country,
    form.postal_code,
  ]
    .filter(Boolean)
    .join(", ");
  const formattedDOB = toDateOnly(form.dob) || "N/A";
  const age = ageFromDateOnly(form.dob) ?? "N/A";
  const formattedHiringDate = form.user_hiring_date
    ? dayjs(form.user_hiring_date).format("YYYY-MM-DD")
    : "-";
  const mobilePhone = formatPhoneNumber(form.mobile);
  const homePhone = formatPhoneNumber(form.home_phone);

  const trainingOptions = assignedTrainings.map((training) => ({
    value: training.tm_key,
    label: training.tm_title,
  }));

  const selfServiceOptions = [
    { value: `/driverlicense/${userId}`, label: "Driver's Licence" },
    { value: `/driverabstract/${userId}`, label: "Driver's Abstract" },
    {
      value: `/task/direct_deposit_authorization/${userId}`,
      label: "Direct Deposit Authorization",
    },
  ];

  return (
    <div className="container-fluid px-2">
      <div className="form-wrapper my-4">
        {/* ✅ Only Admins see this button */}
        {isAdmin && (actionsReady ? (
          <div className="userhome-topbar" role="group" aria-label="User profile actions">
            {hasPermission("UserProfile", "Personal Information", "view") && (
              <button
                type="button"
                className="userhome-account-action"
                data-no-standard-button="true"
                onClick={() => navigate(`/user-account/${userId}`)}
              >
                <i className="fa fa-user-cog" aria-hidden="true"></i>
                <span>Account</span>
              </button>
            )}
            {isOwnerOperatorCategory && hasPermission("UserProfile", "Company Information", "view") && (
              <button type="button" className="userhome-account-action"
                data-no-standard-button="true"
                onClick={() => navigate(`/owner-operator-company/${userId}`)}>
                <i className="fa fa-building" aria-hidden="true" />
                <span>Company Information</span>
              </button>
            )}
            <button
              type="button"
              className="userhome-account-action"
                data-no-standard-button="true"
              onClick={() => navigate(`/status-history/${userId}`)}
            >
              <i className="fa fa-clock" aria-hidden="true"></i>
              <span>Status History</span>
            </button>
          </div>
        ) : (
          <div className="userhome-actions-placeholder" role="status">
            {actionsFailed
              ? "Profile actions unavailable. Refresh to retry."
              : <span className="visually-hidden">Loading profile actions...</span>}
          </div>
        ))}

        {/* Header */}
        <div className="user-profile-summary mb-4">
          <div className="user-avatar-wrap me-4">
            {isAdmin ? (
              <button
                type="button"
                className="user-avatar-trigger"
                onClick={() => profileUploadInputRef.current?.click()}
                disabled={isUploadingProfile}
                title="Upload profile picture"
              >
                <span className={`user-avatar-circle ${!showProfileImage ? "is-empty" : ""}`}>
                  {showProfileImage ? (
                    <img
                      src={profileImageUrl}
                      alt="User"
                      className="user-img"
                      onError={() => setProfileImageBroken(true)}
                    />
                  ) : (
                    <span className="user-avatar-placeholder">
                      {isUploadingProfile ? "Uploading..." : placeholderLabel}
                    </span>
                  )}
                </span>
              </button>
            ) : (
              <div className="user-avatar-trigger">
                <span className={`user-avatar-circle ${!showProfileImage ? "is-empty" : ""}`}>
                  {showProfileImage ? (
                    <img
                      src={profileImageUrl}
                      alt="User"
                      className="user-img"
                      onError={() => setProfileImageBroken(true)}
                    />
                  ) : (
                    <span className="user-avatar-placeholder">{placeholderLabel}</span>
                  )}
                </span>
              </div>
            )}
            <input
              ref={profileUploadInputRef}
              type="file"
              accept="image/*"
              className="d-none"
              onChange={handleProfileUpload}
            />
          </div>
          <div className="user-profile-primary">
            <span className="user-profile-eyebrow">Employee profile</span>
            <h2>{fullName || "User Name"}</h2>
            <p className="user-profile-contact-line">
              {mobilePhone && (
                <>
                  <i className="fas fa-mobile-alt" aria-hidden="true" />
                  <a href={phoneHref(form.mobile)}>{mobilePhone}</a>
                </>
              )}
              {mobilePhone && homePhone && (
                <span className="user-profile-contact-divider" aria-hidden="true" />
              )}
              {homePhone && (
                <>
                  <i className="fas fa-phone-alt" aria-hidden="true" />
                  <a href={phoneHref(form.home_phone)}>{homePhone}</a>
                </>
              )}
              {!mobilePhone && !homePhone && (
                <>
                  <i className="fas fa-phone-alt" aria-hidden="true" />
                  <span>Phone not provided</span>
                </>
              )}
              <span className="user-profile-contact-divider" aria-hidden="true" />
              <i className="fas fa-envelope" aria-hidden="true" />
              {form.email ? (
                <a href={`mailto:${form.email}`}>{form.email}</a>
              ) : (
                "Not provided"
              )}
            </p>
            <p className="user-profile-address-line">
              <i className="fas fa-map-marker-alt" aria-hidden="true" />
              <span>{fullAddress || "Address not provided"}</span>
            </p>
          </div>
          <div className="user-profile-employment">
            <div className="user-profile-facts">
              <div className="user-profile-classification">
                <div><span>Status</span><strong>{getStatusName(form.status) || "Unknown status"}</strong></div>
                <div><span>Category</span><strong>{getCategoryName(form.emp_category) || "Uncategorized"}</strong></div>
                <div><span>Location</span><strong>{getOrgUnitName(form.org_unit) || "No location"}</strong></div>
              </div>
              <p>
                <strong>Employee ID</strong><span>{form.userid_no || "-"}</span>
              </p>
              <p>
                <strong>Hiring Date</strong><span>{formattedHiringDate || "-"}</span>
              </p>
              <p>
                <strong>Birth Date</strong>
                <span>{formattedDOB || "-"}{formattedDOB && age != null ? ` (Age ${age})` : ""}</span>
              </p>
              {isAdmin && canViewSin && (
                <p className="user-profile-sensitive-value">
                  <strong>SSN</strong>
                  <span className="user-profile-sensitive-control">
                    <span>{formatSin(form.sin, showSin)}</span>
                    {form.sin && (
                      <button type="button" className="user-profile-sensitive-toggle" data-no-standard-button onClick={() => setShowSin((prev) => !prev)} aria-label={showSin ? "Hide SSN" : "Show SSN"}>
                        <i className={`fa ${showSin ? "fa-eye-slash" : "fa-eye"}`} aria-hidden="true" />
                      </button>
                    )}
                  </span>
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="row">
          {/* Sidebar */}
          <div className="col-md-3">
            <div className="bg-white p-3 rounded shadow mb-4">
              <div className="video-card">
                <img
                  src="/truck-bg.png"
                  alt="New Way Transport"
                  className="home-hero-image"
                />
              </div>
            </div>
            <div className="userhome-action-card mb-4">
              <div className="userhome-card-heading">
                <span className="userhome-card-kicker">Learning</span>
                <h6>My Training</h6>
              </div>
              <Select
                aria-label="Choose a training module"
                className="userhome-select"
                classNamePrefix="userhome-select"
                isSearchable={false}
                options={trainingOptions}
                placeholder="Choose a module to begin..."
                value={trainingOptions.find((option) => option.value === selectedTraining) || null}
                onChange={handleTrainingSelect}
                noOptionsMessage={() => "No training modules assigned"}
              />
            </div>

            <div className="userhome-action-card mb-4">
              <div className="userhome-card-heading">
                <span className="userhome-card-kicker">Employee tools</span>
                <h6>Self Service</h6>
              </div>
              <Select
                aria-label="Choose a self-service action"
                className="userhome-select"
                classNamePrefix="userhome-select"
                isSearchable={false}
                options={selfServiceOptions}
                placeholder="Update my..."
                value={null}
                onChange={(option) => option?.value && navigate(option.value)}
              />
            </div>
            <div className="file-status-card">
              <div className="file-status-heading">
                <div>
                  <span className="userhome-card-kicker">Overview</span>
                  <h6 className="fs-title">Task Status</h6>
                </div>
                <span className="task-total"><strong>{assignedTotal}</strong> total</span>
              </div>

              <div className="chart-wrapper">
                <DonutFileStatusChart
                  completed={completedCount}
                  almostExpired={almostExpiredCount}
                  expired={expiredCount}
                  review={reviewCount}
                  notStarted={notStartedCount}
                />
              </div>

              <div className="status-counts">
                <span className="badge-complete">
                  <LuBadgeCheck color="#238a5a" size={17} strokeWidth={2.2} />
                  <span>Complete</span><strong>{completedCount}</strong>
                </span>
                <span className="badge-almost-expired">
                  <LuTimerReset color="#3b82a0" size={17} strokeWidth={2.2} />
                  <span>Almost Expired</span><strong>{almostExpiredCount}</strong>
                </span>
                <span className="badge-expired">
                  <LuCalendarX color="#b92534" size={17} strokeWidth={2.2} />
                  <span>Expired</span><strong>{expiredCount}</strong>
                </span>
                <span className="badge-not-started">
                  <LuCircleDashed color="#c93d4b" size={17} strokeWidth={2.2} />
                  <span>Not Started</span><strong>{notStartedCount}</strong>
                </span>
                <span className="badge-review">
                  <span className="text-warning task-review-warning" role="img" aria-label="Ready for review">⚠️</span>
                  <span>Ready for Review</span><strong>{reviewCount}</strong>
                </span>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="col-md-9">
            <div className="row g-3">
              {assignedGroups.map((group) => (
                <div className="col-md-6" key={group.category.id}>
                  <div className="bg-white p-3 rounded shadow h-100">
                    <h6 className="section-title">{group.category.name}</h6>
                    <hr className="section-divider" />
                    {group.tasks
                      .filter(
                        (t) =>
                          !HIDDEN_HOME_TASK_KEYS.has(
                            String(t.t_key || "").toLowerCase(),
                          ) && canViewTask(t),
                      )
                      .map((t) => {
                        const hideIcons = !needsComplete(t);

                        return (
                          <div
                            key={t.t_id}
                            className="hover-item"
                            onClick={() => openTask(t)}
                          >
                            <span className="home-task-label">{t.t_title}</span>
                            {!hideIcons &&
                              (isDone(t) ? (
                                <span className="ms-2">
                                  <LuBadgeCheck color="#198754" size={17} strokeWidth={2.4} />
                                </span>
                            ) : isAlmostExpired(t) ? (
                              <span className="ms-2" title="Almost Expired">
                                <LuTimerReset color="#2563eb" size={17} strokeWidth={2.4} />
                              </span>
                            ) : isExpired(t) ? (
                              <span className="ms-2" title="Expired">
                                <LuCalendarX color="#b92534" size={17} strokeWidth={2.4} />
                              </span>
                            ) : isReview(t) ? (
                                <span className="text-warning ms-2">⚠️</span>
                              ) : (
                                <span className="ms-2" title="Not Started">
                                  <LuCircleDashed color="#dc3545" size={17} strokeWidth={2.4} />
                                </span>
                              ))}
                          </div>
                        );
                      })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserHome;

